/**
 * 회원 알림 설정 — 문구 사전 (한 파일).
 *
 * 문구 정본: rappo 워크트리 `docs/CENTER_MEMBER_EXPIRY_ALERT_SETTING.md` 7-3(레이아웃·문구), 7-5(상태 문구)
 * 시점 키 정본: rappo `src/constants/memberExpiryNotice.constant.ts` 의 `MEMBER_EXPIRY_NOTICE_TIMINGS` (설계 3-2)
 *
 * ⚠️ `MEMBER_NOTIFICATION_TIMING_COPY` 의 키가 곧 "문구 사전 키"다.
 *    화면은 **GET 응답 키 ∩ 이 사전 키**만 행으로 그린다(7-4, `getRenderableTimings`).
 *    서버에 없는 키를 여기 추가하면 행은 안 생기지만(응답에 없으니), 서버에 새 키가 생겼는데
 *    여기 없으면 그 시점은 화면에 보이지 않는다.
 *
 * 문구를 바꿀 때 지켜야 할 것 (설계 7-3):
 *   - 1/3·1/6 예시는 날짜 비교 결과라 하루 앞뒤로 달라질 수 있어 "예)" 로만 쓴다.
 *   - "발급된 센터의 설정을 따라요" 는 쓰지 않는다 — PT 는 담당 강사가 지금 이 센터 소속일 때만 적용된다(Q5).
 *   - "저장하면 다음 발송부터" 라고 쓰지 않는다 — 발송 판정 뒤의 변경은 그날 반영되지 않는다(5-6).
 */
import type { MemberNotificationKind, MemberNotificationKindSendState } from './memberNotificationSettings';

type TimingCopy = {
    /** 행 제목 */
    title: string;
    /** 행 보조 문구. 없으면 null */
    example: string | null;
};

/**
 * 시점 키 → 제목·예시. 키 이름은 서버 상수와 같아야 한다.
 * (PT 와 그룹에 같은 이름 `sevenDaysBefore` 가 있지만 종류가 다르므로 따로 둔다)
 */
export const MEMBER_NOTIFICATION_TIMING_COPY: Record<MemberNotificationKind, Record<string, TimingCopy>> = {
    membershipExpiryNotice: {
        oneThirdLeft: {
            title: '이용 기간이 1/3 남았을 때',
            example: '예) 30일권 만료 10일 전 · 90일권 만료 30일 전',
        },
        oneSixthLeft: {
            title: '이용 기간이 1/6 남았을 때',
            example: '예) 30일권 만료 5일 전 · 90일권 만료 15일 전',
        },
        sevenDaysBefore: {
            title: '만료 7일 전',
            example: null,
        },
    },
    memberTicketExpiryNotice: {
        sevenDaysBefore: { title: '만료 7일 전', example: null },
        threeDaysBefore: { title: '만료 3일 전', example: null },
        oneDayBefore: { title: '만료 1일 전', example: null },
    },
};

type KindCopy = {
    /** 섹션 제목 */
    title: string;
    /** 발송 시각·채널 */
    schedule: string;
    /** 대상 수강권 범위 */
    scope: string;
    /** 항상 보이는 안내(ⓘ). 없으면 null */
    info: string | null;
    /** 끈 시점이 하나라도 있을 때 보이는 경고(⚠) */
    offWarning: string;
    /** 한 종류의 시점을 전부 끄고 저장할 때만 뜨는 확인창 (`isTurningKindAllOff`) */
    confirmAllOff: {
        title: string;
        body: string;
        cancel: string;
        confirm: string;
    };
};

export const MEMBER_NOTIFICATION_KIND_COPY: Record<MemberNotificationKind, KindCopy> = {
    membershipExpiryNotice: {
        title: 'PT 수강권 만료 안내',
        schedule: '매일 오전 8시 · 앱 푸시',
        scope: "'기간 만료 시'·'둘 중 빠른 것' 수강권에만 가요. '회차 소진 시' 수강권에는 보내지 않아요.",
        info: '기간이 짧은 수강권은 두 시점이 같은 날일 수 있어요. 켜 둔 시점이 하나라도 있으면 그날 한 번 보내요. (예: 21일권은 1/3 시점과 만료 7일 전이 같은 날)',
        offWarning:
            '끈 시점에는 안내가 가지 않아요. 기간이 끝나면 수강권이 자동으로 만료돼 수업 신청이 막히니, 필요한 회원에게는 따로 알려 주세요.',
        confirmAllOff: {
            title: 'PT 수강권 만료 안내를 모두 끌까요?',
            body: '기간이 끝나 만료되는 수강권도 회원에게 미리 알리지 않아요. 회원이 만료 사실을 모른 채 수업을 신청하지 못할 수 있어요.',
            cancel: '취소',
            confirm: '모두 끄기',
        },
    },
    memberTicketExpiryNotice: {
        title: '그룹 수강권 만료 안내',
        schedule: '매일 오전 9시 이후 · 앱 푸시, 앱을 쓰지 않는 회원은 카카오 알림톡',
        scope: "'회차 소진 시' 수강권에는 보내지 않아요.",
        info: null,
        offWarning: '끈 시점에는 푸시와 알림톡 모두 가지 않아요. 만료일이 지난 그룹 수강권으로는 예약할 수 없어요.',
        confirmAllOff: {
            title: '그룹 수강권 만료 안내를 모두 끌까요?',
            body: '푸시와 카카오 알림톡이 모두 가지 않아요. 만료일이 지나면 회원이 예약할 수 없어요.',
            cancel: '취소',
            confirm: '모두 끄기',
        },
    },
};

/** 종류 상태 배지 (`getKindSendState`) */
export const MEMBER_NOTIFICATION_SEND_STATE_LABEL: Record<MemberNotificationKindSendState, string> = {
    all: '모두 보냄',
    partial: '일부만 보냄',
    none: '보내지 않음',
};

/** 종류 단위 텍스트 버튼 — 초안만 바꾼다 (`setDraftKindAll`) */
export const MEMBER_NOTIFICATION_BULK_ACTION_LABEL = {
    turnAllOff: '모두 끄기',
    turnAllOn: '모두 켜기',
} as const;

/** 모달 전체 문구 */
export const MEMBER_NOTIFICATION_MODAL_COPY = {
    /** 드롭다운 메뉴명 = 모달 제목 (Q3) */
    title: '회원 알림 설정',
    description: [
        '회원에게 자동으로 가는 수강권 만료 안내를 언제 보낼지 고릅니다.',
        '센터 전체에 적용돼요. 강사에게 가는 알림과 알림센터는 바뀌지 않아요.',
    ],
    /**
     * 푸터 안내 — 설계 7-3 원문을 따른다.
     * 2·3번째 줄은 "껐는데 안내가 갔다"는 문의를 미리 막는다.
     *   - 발송 준비 중 변경: 그날 배치가 이미 설정을 읽었으면 다음 날부터 적용된다(설계 E22).
     *   - PT 소속 강사 조건: 담당 강사가 이 센터 소속이 아니면 끔이 적용되지 않는다(확정 결정 Q5,
     *     서버 centerMemberNotificationSettings.service 의 강사 소속 판정).
     */
    footer: [
        '저장하면 다음 날 발송부터 확실히 적용돼요. 이미 보낸 안내는 취소되지 않아요.',
        '오전 8~9시 발송 준비 중에 바꾸면 그날은 이전 설정으로 나갈 수 있어요.',
        'PT 수강권은 이 센터 소속 강사가 담당하는 수강권에만 적용돼요.',
    ],
    cancel: '취소',
    save: '저장',
    saving: '저장 중',
    changedCount: (count: number) => `변경 ${count}건`,
    retry: '다시 시도',
    close: '닫기',
    /** PT와 그룹을 한 번에 전부 끄는 경우 */
    allOffConfirmTitle: '수강권 만료 안내를 모두 끌까요?',
    /** 변경 후 닫기 */
    discardConfirm: {
        title: '저장하지 않고 닫을까요?',
        cancel: '취소',
        confirm: '닫기',
    },
} as const;

/** 7-5 상태 문구 */
export const MEMBER_NOTIFICATION_STATUS_COPY = {
    load: {
        /** phase 'loading' */
        loading: '불러오는 중',
        /** phase 'unavailable' (GET 404) */
        unavailable: '지금은 이 설정을 사용할 수 없어요.',
        /** phase 'forbidden' (GET 403/406) */
        forbidden: '센터 대표 계정에서만 볼 수 있어요.',
        /** phase 'error' (그 밖·네트워크·응답 모양 이상) */
        error: '설정을 불러오지 못했어요.',
    },
    /** 열람 모드 배너 */
    readOnly: '센터 열람 모드에서는 변경할 수 없어요.',
    /** 저장 200 */
    saved: '저장했어요. 다음 날 발송부터 확실히 적용돼요.',
    save: {
        /** 403(VIEW_ONLY 제외)/406 */
        forbidden: '센터 대표 계정만 바꿀 수 있어요.',
        /** 404 */
        notFound: '지금은 저장할 수 없어요.',
        /** 그 밖 (400·409·5xx·네트워크) */
        error: '저장하지 못했어요. 다시 시도해 주세요.',
    },
} as const;
